import { sessionReminderService } from './sessionReminderService';

/**
 * Reminder Scheduler Service
 * 
 * This service handles the scheduling and processing of session reminders.
 * It can be used with:
 * 1. Cron jobs (server-side)
 * 2. Supabase Edge Functions
 * 3. Vercel Cron Jobs
 * 4. Manual triggers
 */

export class ReminderScheduler {
  private isProcessing = false;
  private intervalId: NodeJS.Timeout | null = null;

  /**
   * Start the reminder processor (for development/testing)
   * In production, use cron jobs or cloud functions instead
   */
  startProcessor(intervalMinutes: number = 5): void {
    if (this.intervalId) {
      console.log('⚠️ Reminder processor is already running');
      return;
    }

    console.log(`🚀 Starting reminder processor with ${intervalMinutes} minute intervals`);
    
    // Process immediately
    this.processReminders();
    
    // Then process every interval
    this.intervalId = setInterval(() => {
      this.processReminders();
    }, intervalMinutes * 60 * 1000);
  }

  /**
   * Stop the reminder processor
   */
  stopProcessor(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('⏹️ Reminder processor stopped');
    }
  }

  /**
   * Process pending reminders (main function called by cron/scheduler)
   */
  async processReminders(): Promise<void> {
    if (this.isProcessing) {
      console.log('⏭️ Reminder processing already in progress, skipping...');
      return;
    }

    this.isProcessing = true;
    
    try {
      console.log('🔄 Starting reminder processing cycle...');
      const startTime = Date.now();
      
      await sessionReminderService.processPendingReminders();
      
      const duration = Date.now() - startTime;
      console.log(`✅ Reminder processing completed in ${duration}ms`);
      
    } catch (error) {
      console.error('❌ Error in reminder processing cycle:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Schedule reminders for a specific session
   * Call this when a session is booked/created
   */
  async scheduleForSession(sessionId: string): Promise<boolean> {
    try {
      console.log(`📅 Scheduling reminders for session: ${sessionId}`);
      return await sessionReminderService.scheduleSessionReminders(sessionId);
    } catch (error) {
      console.error(`❌ Error scheduling reminders for session ${sessionId}:`, error);
      return false;
    }
  }

  /**
   * Cancel reminders for a session
   * Call this when a session is cancelled or rescheduled
   */
  async cancelForSession(sessionId: string): Promise<boolean> {
    try {
      console.log(`❌ Cancelling reminders for session: ${sessionId}`);
      return await sessionReminderService.cancelSessionReminders(sessionId);
    } catch (error) {
      console.error(`❌ Error cancelling reminders for session ${sessionId}:`, error);
      return false;
    }
  }

  /**
   * Send immediate reminder for a session
   * Call this for manual reminder triggers
   */
  async sendImmediateReminder(sessionId: string): Promise<boolean> {
    try {
      console.log(`📧 Sending immediate reminder for session: ${sessionId}`);
      return await sessionReminderService.sendImmediateReminder(sessionId);
    } catch (error) {
      console.error(`❌ Error sending immediate reminder for session ${sessionId}:`, error);
      return false;
    }
  }

  /**
   * Get processing status
   */
  getStatus(): { isProcessing: boolean; isRunning: boolean } {
    return {
      isProcessing: this.isProcessing,
      isRunning: this.intervalId !== null
    };
  }
}

// Export singleton instance
export const reminderScheduler = new ReminderScheduler();

// Auto-start in development (optional)
if (process.env.NODE_ENV === 'development' && typeof window === 'undefined') {
  // Only start in server-side development environment
  console.log('🔧 Development mode: Starting reminder processor');
  reminderScheduler.startProcessor(2); // Check every 2 minutes in development
}

// Cleanup on process exit
if (typeof process !== 'undefined') {
  process.on('SIGINT', () => {
    console.log('🛑 Shutting down reminder processor...');
    reminderScheduler.stopProcessor();
    process.exit(0);
  });

  process.on('SIGTERM', () => {
    console.log('🛑 Shutting down reminder processor...');
    reminderScheduler.stopProcessor();
    process.exit(0);
  });
}